import {Panel} from '@github-ui/docked-side-panel/Panel'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {SidePanel} from '@github-ui/side-panel'
import {HistoryIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import type {ReactNode} from 'react'
import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {IssueTimelineErrorFallback} from '../IssueTimelineErrorFallback'
import {IssueTimelineLoading} from '../IssueTimelineLoading'
import {useActivityPanel} from './ActivityPanelContext'
import styles from './TimelineActivityMenu.module.css'

type TimelineActivityMenuProps = {
  initiallyOpen: boolean | null
  timeline: ReactNode
}

export function TimelineActivityMenu({initiallyOpen, timeline}: TimelineActivityMenuProps) {
  const {closePanel, open, openPanel, panelTarget} = useActivityPanel()
  const [overlayMounted, setOverlayMounted] = useState(initiallyOpen === true)
  const initialOpenStateAppliedRef = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (initialOpenStateAppliedRef.current || initiallyOpen === null) return
    initialOpenStateAppliedRef.current = true
    if (initiallyOpen) openPanel()
  }, [initiallyOpen, openPanel])

  const handleOpen = useCallback(() => {
    setOverlayMounted(true)
    openPanel()
    sendEvent('issue_viewer.activity_panel.opened')
  }, [openPanel])

  const panelContent = (
    <ErrorBoundary boundaryName="issue-activity-timeline" fallback={<IssueTimelineErrorFallback />}>
      <Suspense fallback={<IssueTimelineLoading delayedShow />}>{timeline}</Suspense>
    </ErrorBoundary>
  )

  return (
    <>
      <Button ref={buttonRef} leadingVisual={HistoryIcon} onClick={handleOpen}>
        Activity
      </Button>
      {open &&
        panelTarget &&
        createPortal(
          <Panel
            heading="Activity"
            headingLevel={2}
            closeButtonRef={closeButtonRef}
            returnFocusRef={buttonRef}
            onClose={closePanel}
          >
            <Panel.Body className={styles.body}>{panelContent}</Panel.Body>
          </Panel>,
          panelTarget,
        )}
      {!panelTarget && overlayMounted && (
        <SidePanel
          open={open}
          onClose={closePanel}
          onAfterClose={() => setOverlayMounted(false)}
          returnFocusRef={buttonRef}
          initialFocusRef={headingRef}
          bodyScrollLockDisabled
          panelClassName={styles.panel}
          headerClassName={styles.header}
          closeButtonClassName={styles.closeButton}
          aria-label="Issue activity"
        >
          <SidePanel.Heading>
            <Heading ref={headingRef} tabIndex={-1} as="h2" className={styles.heading}>
              Activity
            </Heading>
          </SidePanel.Heading>
          <div className={styles.body}>{panelContent}</div>
        </SidePanel>
      )}
    </>
  )
}
